import type { ClassSummary } from '@school-erp/types';
import { INVOICE_FILTERS } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor, parseMajor } from '../../lib/money';

interface Invoice {
  id: string;
  invoiceNumber: string;
  studentName: string;
  admissionNumber: string;
  className: string | null;
  status: 'issued' | 'cancelled';
  totalMinor: number;
  paidMinor: number;
  balanceMinor: number;
  overdueMinor: number;
}
interface InvoiceDetail extends Invoice {
  currency: string;
  lines: { id: string; kind: string; description: string; amountMinor: number; dueDate: string }[];
  payments: {
    paymentId: string;
    receiptNumber: string;
    paidOn: string;
    method: string;
    status: string;
    amountMinor: number;
  }[];
}

export function InvoicesTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [classId, setClassId] = useState('');
  const [selected, setSelected] = useState('');
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const classes = useQuery({
    queryKey: ['classes', schoolId],
    queryFn: () => api<ClassSummary[]>(`/schools/${schoolId}/classes`),
  });
  const list = useQuery({
    queryKey: ['fees', schoolId, 'invoices', filter, page],
    queryFn: async () => {
      const res = await apiList<Invoice>(`/schools/${schoolId}/invoices`, {
        query: { filter, page, pageSize: 20 },
      });
      return { ...res, currency: (res as unknown as { currency: string }).currency };
    },
  });
  const currency = list.data?.currency ?? 'USD';

  const generate = useMutation({
    mutationFn: () =>
      api<{
        invoicesCreated: number;
        linesAdded: number;
        upToDate: number;
        noStructure: number;
        skippedCancelled: number;
      }>(`/schools/${schoolId}/invoices/generate`, { method: 'POST', body: { classId } }),
    onSuccess: (r) => {
      setMessage({
        kind: 'ok',
        text: t('fees.generated', {
          created: r.invoicesCreated,
          added: r.linesAdded,
          upToDate: r.upToDate,
          none: r.noStructure,
          cancelled: r.skippedCancelled,
        }),
      });
      void queryClient.invalidateQueries({ queryKey: ['fees', schoolId] });
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  return (
    <section aria-label={t('fees.tab.invoices')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <div className="flex flex-wrap items-end gap-4">
        {can('invoices.generate', schoolId) && (
          <div className="flex items-end gap-2">
            <div>
              <label htmlFor="gen-class" className="block text-sm font-medium">
                {t('fees.class')}
              </label>
              <select
                id="gen-class"
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
                className={selectClass}
              >
                <option value="">—</option>
                {classes.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className={primaryButton}
              disabled={!classId || generate.isPending}
              onClick={() => generate.mutate()}
            >
              {t('fees.generate')}
            </button>
          </div>
        )}
        <div>
          <label htmlFor="inv-filter" className="block text-sm font-medium">
            {t('fees.status')}
          </label>
          <select
            id="inv-filter"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            <option value="">{t('fees.filter.all')}</option>
            {INVOICE_FILTERS.map((f) => (
              <option key={f} value={f}>
                {t(`fees.filter.${f}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {list.isError && <Notice kind="error">{errorMessage(list.error)}</Notice>}
      {list.data?.data.length === 0 && <p className="text-slate-600">{t('fees.noInvoices')}</p>}
      {list.data && list.data.data.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th scope="col" className="p-2">
                  {t('fees.invoice')}
                </th>
                <th scope="col" className="p-2">
                  {t('fees.student')}
                </th>
                <th scope="col" className="p-2">
                  {t('fees.class')}
                </th>
                <th scope="col" className="p-2 text-right">
                  {t('fees.total')}
                </th>
                <th scope="col" className="p-2 text-right">
                  {t('fees.paid')}
                </th>
                <th scope="col" className="p-2 text-right">
                  {t('fees.balance')}
                </th>
                <th scope="col" className="p-2 text-right">
                  {t('fees.overdue')}
                </th>
                <th scope="col" className="p-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.data.data.map((i) => (
                <tr
                  key={i.id}
                  className={i.status === 'cancelled' ? 'text-slate-400 line-through' : ''}
                >
                  <td className="p-2 font-mono text-xs">{i.invoiceNumber}</td>
                  <th scope="row" className="p-2 font-medium">
                    {i.studentName}
                  </th>
                  <td className="p-2">{i.className ?? '—'}</td>
                  <td className="p-2 text-right">{formatMinor(i.totalMinor, currency)}</td>
                  <td className="p-2 text-right">{formatMinor(i.paidMinor, currency)}</td>
                  <td className="p-2 text-right">{formatMinor(i.balanceMinor, currency)}</td>
                  <td className="p-2 text-right">
                    {i.overdueMinor > 0 ? formatMinor(i.overdueMinor, currency) : '—'}
                  </td>
                  <td className="p-2 text-right">
                    <button
                      type="button"
                      className={secondaryButton}
                      aria-label={`${t('fees.view')}: ${i.invoiceNumber}`}
                      onClick={() => setSelected(i.id)}
                    >
                      {t('fees.view')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {list.data && list.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={list.data.meta.totalPages} onPage={setPage} />
      )}
      {selected && <InvoicePanel key={selected} schoolId={schoolId} invoiceId={selected} />}
    </section>
  );
}

function InvoicePanel({ schoolId, invoiceId }: { schoolId: string; invoiceId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const key = ['fees', schoolId, 'invoice', invoiceId];
  const [message, setMessage] = useState<string | null>(null);
  const [adjust, setAdjust] = useState({ description: '', amount: '' });
  const [reason, setReason] = useState('');
  const [adjustError, setAdjustError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: key,
    queryFn: () => api<InvoiceDetail>(`/schools/${schoolId}/invoices/${invoiceId}`),
  });
  const changed = () => {
    setMessage(null);
    void queryClient.invalidateQueries({ queryKey: ['fees', schoolId] });
  };
  const apply = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/invoices/${invoiceId}/adjustments`, { method: 'POST', body }),
    onSuccess: () => {
      setAdjust({ description: '', amount: '' });
      changed();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const cancel = useMutation({
    mutationFn: () =>
      api(`/schools/${schoolId}/invoices/${invoiceId}/cancel`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => {
      setReason('');
      changed();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });

  if (detail.isPending) return <p role="status">{t('common.loading')}</p>;
  if (detail.isError) return <Notice kind="error">{errorMessage(detail.error)}</Notice>;
  const inv = detail.data;
  const cur = inv.currency;
  const canAdjust = can('fees.adjust', schoolId) && inv.status === 'issued';

  return (
    <section
      aria-label={inv.invoiceNumber}
      className="space-y-4 rounded-lg border border-slate-200 bg-white p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {inv.invoiceNumber} · {inv.studentName}
        </h2>
        <span className="text-sm text-slate-700">
          {t('fees.balance')}: <strong>{formatMinor(inv.balanceMinor, cur)}</strong>
        </span>
      </div>
      {message && <Notice kind="error">{message}</Notice>}
      <table className="w-full text-left text-sm">
        <caption className="sr-only">{t('fees.lines')}</caption>
        <tbody className="divide-y divide-slate-100">
          {inv.lines.map((l) => (
            <tr key={l.id}>
              <td className="p-2">{l.description}</td>
              <td className="p-2">{l.dueDate}</td>
              <td className="p-2 text-right">{formatMinor(l.amountMinor, cur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {inv.payments.length > 0 && (
        <div>
          <h3 className="font-semibold">{t('fees.receipts')}</h3>
          <ul className="text-sm text-slate-700">
            {inv.payments.map((p) => (
              <li key={p.paymentId}>
                {p.receiptNumber} · {p.paidOn} · {formatMinor(p.amountMinor, cur)}
                {p.status === 'void' ? ` (${t('fees.statusVoid')})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
      {canAdjust && (
        <form
          noValidate
          aria-label={t('fees.adjust')}
          onSubmit={(e) => {
            e.preventDefault();
            const negative = adjust.amount.trim().startsWith('-');
            const major = negative ? adjust.amount.trim().slice(1) : adjust.amount;
            const minor = parseMajor(major, cur);
            if (!adjust.description.trim() || minor === null || minor === 0)
              return setAdjustError(t('fees.invalidAmount'));
            setAdjustError(null);
            apply.mutate({
              description: adjust.description.trim(),
              amountMinor: negative ? -minor : minor,
            });
          }}
          className="grid gap-4 sm:grid-cols-3"
        >
          <TextField
            label={t('fees.adjustDescription')}
            value={adjust.description}
            onChange={(e) => setAdjust({ ...adjust, description: e.target.value })}
          />
          <TextField
            label={t('fees.adjustAmount')}
            inputMode="decimal"
            value={adjust.amount}
            error={adjustError}
            onChange={(e) => setAdjust({ ...adjust, amount: e.target.value })}
          />
          <div className="self-end">
            <button type="submit" className={secondaryButton} disabled={apply.isPending}>
              {t('fees.adjustApply')}
            </button>
          </div>
        </form>
      )}
      {canAdjust && (
        <div className="flex flex-wrap items-end gap-3">
          <TextField
            className="min-w-64 flex-1"
            label={t('fees.cancelReason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button
            type="button"
            className="rounded border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            disabled={!reason.trim() || cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            {t('fees.cancelInvoice')}
          </button>
        </div>
      )}
    </section>
  );
}
